import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  type ChartAccount,
  type CrateSummary,
  createChartAccount,
  installCrate,
  enableAccountingHost,
  listChartAccounts,
  listCrates,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";
import { useOrganization } from "@/lib/organization";

type Step = "crates" | "chart" | "opening" | "done";

const STEPS: Step[] = ["crates", "chart", "opening", "done"];

const STEP_TITLES: Record<Step, string> = {
  crates: "Set up your books",
  chart: "Add your accounts",
  opening: "Starting balances",
  done: "You're set up",
};

import { STARTER_ACCOUNTS, validateStarterChart } from "./starter-chart";

// The setup choices whose HTTP surfaces this app calls: every /finance/*
// fetcher in lib/api.ts lives under one of these resource roots, and the
// handlers check a per-organization installation row. Invoicing gates
// progress; spending plans and the books are
// offered alongside it, and any other catalog entry can be added as a
// companion. Labels describe outcomes so a new owner never sees crate
// names; the keys are the exact catalog entries installed.
const BOOKS_SETUP = [
  {
    key: "invoicing",
    label: "Invoicing and payments",
    detail:
      "Send customer invoices, record payments, and see what you are owed.",
  },
  {
    key: "budgets",
    label: "Spending plans",
    detail: "Set budgets and compare them with what you actually spent.",
  },
  {
    key: "accounting",
    label: "Books and reports",
    detail: "Keep the ledger and run profit, cash-flow, and balance reports.",
  },
] as const;

const REQUIRED_KEYS = BOOKS_SETUP.map((item) => item.key);

function describeError(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { scope } = useOrganization();
  const [step, setStep] = useState<Step>("crates");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [crates, setCrates] = useState<CrateSummary[]>([]);
  const [cratesStatus, setCratesStatus] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [cratesReload, setCratesReload] = useState(0);
  const [accounts, setAccounts] = useState<ChartAccount[] | null>(null);
  const retryRef = useRef<(() => void) | null>(null);
  const organizationId = scope?.organization_id ?? "";

  const stepIndex = STEPS.indexOf(step);

  const clearFailure = () => {
    setError(null);
    retryRef.current = null;
  };

  const fail = (cause: unknown, retry: () => void) => {
    setError(describeError(cause));
    retryRef.current = retry;
  };

  const goBack = () => {
    clearFailure();
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1]);
  };

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setPending(true);
    setCratesStatus("loading");
    setError(null);
    retryRef.current = null;
    listCrates(organizationId)
      .then((listed) => {
        if (cancelled) return;
        setCrates(listed);
        setCratesStatus("ready");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setCratesStatus("error");
        setError(describeError(cause));
        retryRef.current = () => setCratesReload((value) => value + 1);
      })
      .finally(() => {
        if (!cancelled) setPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, cratesReload]);

  const refreshCrates = async () => {
    setPending(true);
    clearFailure();
    try {
      setCrates(
        await listCrates(organizationId),
      );
      setCratesStatus("ready");
    } catch (cause) {
      fail(cause, () => void refreshCrates());
    } finally {
      setPending(false);
    }
  };

  const installRequired = async (key: string) => {
    clearFailure();
    setPending(true);
    try {
      await installCrate(key, organizationId);
      await refreshCrates();
    } catch (cause) {
      fail(cause, () => void installRequired(key));
    } finally {
      setPending(false);
    }
  };

  const crateReady = (key: string): boolean =>
    crates.some((c) => c.key === key && c.access === "available");

  // Everything in the live catalog that isn't part of the required set —
  // offered as optional companions on the same step.
  const companions = crates.filter(
    (c) => !(REQUIRED_KEYS as readonly string[]).includes(c.key),
  );

  const confirmCrates = async () => {
    clearFailure();
    setPending(true);
    try {
      await enableAccountingHost(organizationId);
      setAccounts(await listChartAccounts());
      setStep("chart");
    } catch (cause) {
      fail(cause, () => void confirmCrates());
    } finally {
      setPending(false);
    }
  };

  const seedStarterChart = async () => {
    clearFailure();
    setPending(true);
    try {
      // Skip codes the entity already has — a half-finished first attempt
      // must be safely retryable instead of 409ing on duplicates.
      const current = await listChartAccounts();
      validateStarterChart(current);
      const existing = new Set(current.map((a) => a.code));
      for (const account of STARTER_ACCOUNTS) {
        if (!existing.has(account.code)) await createChartAccount(account);
      }
      setAccounts(await listChartAccounts());
    } catch (cause) {
      fail(cause, () => void seedStarterChart());
    } finally {
      setPending(false);
    }
  };

  const finish = async () => {
    clearFailure();
    setPending(true);
    try {
      setStep("opening");
    } catch (cause) {
      fail(cause, () => void finish());
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-xl flex-col justify-center gap-6 px-6 py-16">
      <div>
        <p className="text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
          Setup · step {stepIndex + 1} of {STEPS.length}
        </p>
        <PageHeader title={STEP_TITLES[step]} className="mt-1" />
        <ol className="mt-4 flex gap-1.5" aria-label="Setup progress">
          {STEPS.map((s, index) => (
            <li
              key={s}
              aria-current={s === step ? "step" : undefined}
              className={
                "h-1.5 flex-1 rounded-full " +
                (index <= stepIndex
                  ? "bg-[var(--action)]"
                  : "bg-[var(--border)]")
              }
            />
          ))}
        </ol>
      </div>

      {error ? (
        <div
          role="alert"
          className="surface grid gap-2 rounded-xl border border-[var(--danger)] p-4 text-sm break-words text-[var(--danger)]"
        >
          <p>{error}</p>
          {retryRef.current ? (
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={pending}
                onClick={() => retryRef.current?.()}
              >
                Retry
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {step === "crates" ? (
        cratesStatus === "loading" && crates.length === 0 ? (
          <output className="block text-sm text-[var(--muted-foreground)]">
            Loading setup options…
          </output>
        ) : cratesStatus === "error" && crates.length === 0 ? null : (
          <div className="grid gap-4">
            <p className="text-sm text-[var(--muted-foreground)]">
              These tools power the finance pages for this organization.
              Invoicing is required to continue; the others can be set up now
              or later.
            </p>
            <ul className="grid gap-2">
              {BOOKS_SETUP.map((item) => {
                const row = crates.find((c) => c.key === item.key);
                const ready = crateReady(item.key);
                return (
                  <li
                    key={item.key}
                    className="surface flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{item.label}</div>
                      <div className="text-xs text-[var(--muted-foreground)]">
                        {item.detail}
                      </div>
                      <div className="mt-0.5 text-xs text-[var(--muted-foreground)]">
                        {ready
                          ? "Ready to use."
                          : row
                            ? "Not set up yet."
                            : "Not available for this organization yet."}
                      </div>
                    </div>
                    {ready ? (
                      <span
                        role="img"
                        aria-label={`${item.label} is ready`}
                        className="shrink-0 text-sm font-semibold text-[var(--action)]"
                      >
                        ✓
                      </span>
                    ) : (
                      <Button
                        type="button"
                        className="shrink-0"
                        disabled={pending || !row}
                        onClick={() => void installRequired(item.key)}
                      >
                        Set up
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
            {companions.length > 0 ? (
              <>
                <h3 className="text-sm font-semibold">More tools (optional)</h3>
                <ul className="grid gap-2">
                  {companions.map((crate) => {
                    const ready = crate.access === "available";
                    const label =
                      crate.display_name || humanizeLabel(crate.key);
                    return (
                      <li
                        key={crate.key}
                        className="surface flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold">{label}</div>
                          {crate.summary ? (
                            <div className="text-xs text-[var(--muted-foreground)]">
                              {crate.summary}
                            </div>
                          ) : null}
                          <div className="mt-0.5 text-xs text-[var(--muted-foreground)]">
                            {ready ? "Ready to use." : "Not set up yet."}
                          </div>
                        </div>
                        {ready ? (
                          <span
                            role="img"
                            aria-label={`${label} is ready`}
                            className="shrink-0 text-sm font-semibold text-[var(--action)]"
                          >
                            ✓
                          </span>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="shrink-0"
                            disabled={pending}
                            onClick={() => void installRequired(crate.key)}
                          >
                            Add
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
            <StepNav
              pending={pending}
              onBack={goBack}
              nextLabel="Continue"
              nextDisabled={!crateReady("invoicing")}
              onNext={confirmCrates}
            />
          </div>
        )
      ) : null}

      {step === "chart" ? (
        <div className="grid gap-4">
          {accounts === null ? (
            <output className="block text-sm text-[var(--muted-foreground)]">
              Loading your accounts…
            </output>
          ) : accounts.length > 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {accounts.length} account{accounts.length === 1 ? "" : "s"}{" "}
              already on the books — you can manage the full chart on the Chart
              of accounts page later.
            </p>
          ) : (
            <>
              <p className="text-sm text-[var(--muted-foreground)]">
                No accounts yet. Create a starter set (cash, receivables,
                payables, equity, revenue, expenses) — or skip and add them
                manually later.
              </p>
              <div>
                <Button
                  type="button"
                  disabled={pending}
                  onClick={() => void seedStarterChart()}
                >
                  {pending ? "Creating…" : "Create starter accounts"}
                </Button>
              </div>
            </>
          )}
          <StepNav
            pending={pending}
            onBack={goBack}
            nextLabel="Continue"
            onNext={finish}
          />
        </div>
      ) : null}

      {step === "opening" ? (
        <OpeningBalancesStep
          pending={pending}
          setPending={setPending}
          setError={setError}
          onBack={goBack}
          onDone={() => setStep("done")}
        />
      ) : null}

      {step === "done" ? (
        <div className="grid gap-4">
          <p className="text-sm text-[var(--muted-foreground)]">
            Setup saved — you can change the legal entity from the header at
            any time.
          </p>
          <div>
            <Button type="button" onClick={() => void navigate({ to: "/" })}>
              Open overview
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StepNav({
  pending,
  onBack,
  canBack = true,
  nextLabel,
  onNext,
  nextDisabled,
}: {
  pending: boolean;
  onBack?: () => void;
  canBack?: boolean;
  nextLabel: string;
  onNext?: () => void;
  nextDisabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {canBack && onBack ? (
        <Button
          type="button"
          variant="ghost"
          disabled={pending}
          onClick={onBack}
        >
          Back
        </Button>
      ) : (
        <span />
      )}
      <Button
        type={onNext ? "button" : "submit"}
        disabled={pending || nextDisabled}
        onClick={onNext}
      >
        {pending ? "Working…" : nextLabel}
      </Button>
    </div>
  );
}

function OpeningBalancesStep({
  pending,
  onBack,
  onDone,
}: {
  pending: boolean;
  setPending: (value: boolean) => void;
  setError: (value: string | null) => void;
  onBack: () => void;
  onDone: () => void;
}) {
  return (
    <div className="grid gap-4">
      <p className="text-sm text-[var(--muted-foreground)]">
        Starting balances are one journal dated the day before these books
        start. Receivables on that journal must equal open invoices, and
        payables must equal open bills. Saving keeps a draft until you post it
        into the books.
      </p>
      <p className="text-sm">
        <a href="/opening-balances" className="text-[var(--action)] underline-offset-4 hover:underline">
          Enter opening balances
        </a>
      </p>
      <StepNav
        pending={pending}
        onBack={onBack}
        nextLabel="Continue"
        onNext={onDone}
      />
    </div>
  );
}
